import { Alert, Button, Field, Input, QueryError, SkeletonBlock, cx, shortHash } from "@unizpay/ui";
import { ArrowRight } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { NetworkTokenPicker } from "../../../components/NetworkTokenPicker";
import { useAccountNetworks, useWallet } from "../../../lib/queries";
import {
  ADDRESS_FORMAT,
  addressFamily,
  isAddressOf,
  outputTokens,
  preferredToken,
  settlementNetworks,
} from "../../../lib/settlement";
import type { Network, UserWallet } from "../../../lib/types";
import { BackButton, StepCard } from "../StepChrome";
import type { PayoutDraft, PayoutMethod } from "../draft";

function MethodOption({
  value,
  current,
  title,
  text,
  disabled,
  onSelect,
}: {
  value: PayoutMethod;
  current: PayoutMethod;
  title: string;
  text: ReactNode;
  disabled?: boolean;
  onSelect: (method: PayoutMethod) => void;
}) {
  const selected = value === current;
  return (
    <label
      className={cx(
        "flex items-start gap-3 rounded-lg border bg-surface px-4 py-3",
        selected ? "border-accent ring-1 ring-accent" : "border-line",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-line-strong",
      )}
    >
      <input
        type="radio"
        name="payout_method"
        value={value}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(value)}
        className="mt-0.5 size-4 accent-[var(--accent)]"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs text-fg-muted">{text}</span>
      </span>
    </label>
  );
}

function PayoutForm({
  payout,
  networks,
  wallet,
  setPayout,
  next,
  back,
}: {
  payout: PayoutDraft;
  networks: Network[];
  wallet: UserWallet | null;
  setPayout: (patch: Partial<PayoutDraft>) => void;
  next: () => void;
  back: () => void;
}) {
  const [errors, setErrors] = useState<{ network?: string; address?: string }>({});
  const selected = networks.find((n) => n.network === payout.network);
  const first = networks[0];
  const family = addressFamily(selected);
  // The UnizPay wallet is on EVM networks only.
  const walletUsable = Boolean(wallet) && family === "evm";

  // Start from the first network (and its preferred token) once they load.
  useEffect(() => {
    if (!selected && first) {
      setPayout({
        network: first.network,
        networkName: first.name,
        token: preferredToken(outputTokens(first)),
      });
    }
  }, [selected, first, setPayout]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const found: typeof errors = {};
    if (!payout.network || !payout.token) found.network = "Choose a network and token.";
    if (payout.method === "own") {
      if (!isAddressOf(family, payout.address)) found.address = ADDRESS_FORMAT[family].error;
    } else if (!wallet) {
      found.address = "Your UnizPay wallet could not be loaded. Use your own wallet, or try again.";
    }
    setErrors(found);
    if (Object.keys(found).length === 0) next();
  }

  return (
    <StepCard
      onSubmit={onSubmit}
      title="Where should payments go?"
      description="Every live payment settles straight to this wallet, converted to the token you choose. UnizPay never holds your funds."
      footer={
        <>
          <BackButton onClick={back} />
          <Button type="submit">
            Continue <ArrowRight aria-hidden="true" />
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <fieldset className="space-y-2.5">
          <legend className="mb-2 text-[0.8125rem] font-medium">Payout wallet</legend>
          <MethodOption
            value="own"
            current={payout.method}
            title="My own wallet"
            text="Any wallet you control, such as a hardware wallet or an exchange deposit address."
            onSelect={(method) => setPayout({ method })}
          />
          <MethodOption
            value="unizpay"
            current={payout.method}
            title="My UnizPay wallet"
            text={
              family !== "evm"
                ? `Not available on ${selected?.name ?? "this network"} yet. Use your own wallet.`
                : wallet
                  ? `The wallet that came with your account, ${shortHash(wallet.address)}. Protected by your PIN.`
                  : "The wallet that came with your account could not be loaded."
            }
            disabled={!walletUsable}
            onSelect={(method) => setPayout({ method })}
          />
        </fieldset>

        <NetworkTokenPicker
          networks={networks.map((n) => ({ value: n.network, label: n.name }))}
          network={payout.network}
          onNetworkChange={(network) => {
            const n = networks.find((x) => x.network === network);
            if (!n) return;
            const available = outputTokens(n);
            setPayout({
              network: n.network,
              networkName: n.name,
              token: available.includes(payout.token) ? payout.token : preferredToken(available),
              ...(addressFamily(n) !== "evm" && { method: "own" as const }),
            });
          }}
          tokens={outputTokens(selected)}
          token={payout.token}
          onTokenChange={(token) => setPayout({ token })}
          tokenLabel="Receive as"
          error={errors.network}
        />

        {payout.method === "own" ? (
          <Field label="Wallet address" hint="A wallet you control on that network." error={errors.address}>
            {(p) => (
              <Input
                {...p}
                name="address"
                spellCheck={false}
                autoComplete="off"
                placeholder={ADDRESS_FORMAT[family].placeholder}
                className="font-mono"
                value={payout.address}
                onChange={(e) => {
                  setPayout({ address: e.target.value });
                  setErrors((x) => ({ ...x, address: undefined }));
                }}
              />
            )}
          </Field>
        ) : (
          errors.address && <p className="text-[0.8125rem] text-danger">{errors.address}</p>
        )}

        <p className="text-xs text-fg-muted">
          Every payment is converted to this token. To receive some tokens as they are, or to change where
          funds that cannot be converted go, use Settings › Payouts later.
        </p>
      </div>
    </StepCard>
  );
}

export function PayoutStep({
  payout,
  setPayout,
  next,
  back,
}: {
  payout: PayoutDraft;
  setPayout: (patch: Partial<PayoutDraft>) => void;
  next: () => void;
  back: () => void;
}) {
  const networks = useAccountNetworks("live");
  const wallet = useWallet();
  const live = networks.data ? settlementNetworks(networks.data) : [];
  const unavailable = networks.isSuccess && live.length === 0;

  useEffect(() => {
    if (unavailable !== payout.unavailable) setPayout({ unavailable });
  }, [unavailable, payout.unavailable, setPayout]);

  if (networks.isPending || wallet.isPending)
    return <SkeletonBlock label="Loading payout options" lines={4} />;
  if (networks.isError) {
    return (
      <StepCard
        title="Where should payments go?"
        description="Choose the wallet your live payments settle to."
        footer={<BackButton onClick={back} />}
      >
        <QueryError
          error={networks.error}
          title="Could not load networks"
          onRetry={() => networks.refetch()}
          retrying={networks.isFetching}
        />
      </StepCard>
    );
  }

  if (unavailable) {
    return (
      <StepCard
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
        title="Where should payments go?"
        description="Every live payment settles straight to your wallet. UnizPay never holds your funds."
        footer={
          <>
            <BackButton onClick={back} />
            <Button type="submit">
              Continue <ArrowRight aria-hidden="true" />
            </Button>
          </>
        }
      >
        <Alert tone="info" title="Live networks are not enabled on this server yet">
          Finish setting up now, and add your payout wallet in Settings › Payouts once live payments are
          available.
        </Alert>
      </StepCard>
    );
  }

  return (
    <PayoutForm
      payout={payout}
      networks={live}
      wallet={wallet.data ?? null}
      setPayout={setPayout}
      next={next}
      back={back}
    />
  );
}
